import * as z from "zod/mini";

import * as resolved_topic from "../shared/src/resolved_topic.ts";
import * as emoji from "./emoji.ts";

// Only built-in glyphs; these are decorative, never evidence of access.
export const icons = [
    "globe",
    "lock",
    "hashtag",
    "star",
    "heart",
    "lightbulb",
    "check",
    "file-text",
    "smile-smaller",
    "users",
    "recent",
    "inbox",
    "browse-channels",
] as const;
const color_schema = z.nullable(z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/)));
export const color_fields = ["icon_color_light", "icon_color_dark", "text_color_light", "text_color_dark"] as const;
export const appearance_schema = z.object({
    icon: z.nullable(z.enum([...icons, "custom", "emoji"])),
    icon_color: z.nullable(z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/))),
    icon_color_light: z.optional(color_schema),
    icon_color_dark: z.optional(color_schema),
    text_color_light: z.optional(color_schema),
    text_color_dark: z.optional(color_schema),
    emoji: z.optional(z.nullable(z.object({
        emoji_name: z.string(),
        emoji_code: z.string(),
        reaction_type: z.enum(["unicode_emoji", "realm_emoji", "zulip_extra_emoji"]),
    }))),
});
export type Appearance = z.infer<typeof appearance_schema>;
// Display metadata is server-owned, not an editable permission setting.
export const display_appearance_schema = z.extend(appearance_schema, {
    is_restricted: z._default(z.boolean(), false),
    has_description: z.optional(z.boolean()),
    icon_url: z.optional(z.nullable(z.string().check(
        z.regex(/^\/json\/topic_icons\/[0-9]+\/[0-9a-f]{32}$/),
    ))),
});
export type DisplayAppearance = z.infer<typeof display_appearance_schema>;
export const topic_appearance_schema = z.extend(display_appearance_schema, {
    stream_id: z.number(),
    topic_name: z.string(),
});

const appearances = new Map<string, DisplayAppearance>();

function key(stream_id: number, topic: string): string {
    const trimmed = topic.trim();
    const normalized = trimmed === "(no topic)" || trimmed === "general chat" ? "" : trimmed;
    return JSON.stringify([stream_id, normalized.toLowerCase()]);
}

export function set(
    stream_id: number,
    topic: string,
    appearance: z.input<typeof display_appearance_schema>,
): void {
    const parsed = display_appearance_schema.safeParse(appearance);
    if (!parsed.success || (
        parsed.data.icon === null && !parsed.data.is_restricted && !parsed.data.has_description &&
        !color_fields.some((field) => parsed.data[field] != null)
    )) {
        appearances.delete(key(stream_id, topic));
        return;
    }

    appearances.set(key(stream_id, topic), parsed.data);
}

export function text_style(appearance?: DisplayAppearance): string {
    return (["light", "dark"] as const).map((theme) => {
        const color = appearance?.[`text_color_${theme}`];
        return color ? `--topic-text-${theme}: ${color};` : "";
    }).join("");
}

export function icon_style(appearance: Appearance): string {
    const light = appearance.icon_color_light === undefined ? appearance.icon_color : appearance.icon_color_light;
    const dark = appearance.icon_color_dark === undefined ? appearance.icon_color : appearance.icon_color_dark;
    return `color: light-dark(${light ?? "var(--color-text-default)"}, ${dark ?? "var(--color-text-default)"})`;
}

export function emoji_details(appearance: Appearance): emoji.EmojiRenderingDetails | undefined {
    return appearance.emoji ? emoji.get_emoji_details_for_rendering(appearance.emoji) : undefined;
}

export function get(stream_id: number, topic: string): DisplayAppearance | undefined {
    return appearances.get(key(stream_id, topic));
}

export function has_icon(stream_id: number, topic: string): boolean {
    const appearance = get(stream_id, topic);
    return Boolean(appearance?.icon && (appearance.icon !== "custom" || appearance.icon_url));
}

export function display_name(stream_id: number, topic: string, name: string): string {
    return has_icon(stream_id, topic) && resolved_topic.is_resolved(topic)
        ? resolved_topic.display_parts(name)[1]
        : name;
}

// Remove only the canonical prefix, including when it crosses search highlights.
// The topic name, URL, and any remaining checkmarks are deliberately untouched.
export function remove_display_prefix(element: HTMLElement): void {
    if (!resolved_topic.is_resolved(element.textContent ?? "")) {
        return;
    }
    const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let remaining = resolved_topic.RESOLVED_TOPIC_PREFIX.length;
    while (remaining > 0 && walker.nextNode()) {
        const node = walker.currentNode;
        const text = node.textContent ?? "";
        const count = Math.min(remaining, text.length);
        node.textContent = text.slice(count);
        remaining -= count;
    }
}

export function initialize(entries: z.input<typeof topic_appearance_schema>[]): void {
    appearances.clear();
    for (const entry of entries) {
        set(entry.stream_id, entry.topic_name, entry);
    }
}

export function remove_for_streams(stream_ids: Set<number>): void {
    for (const entry_key of appearances.keys()) {
        const [stream_id] = JSON.parse(entry_key) as [number, string];
        if (stream_ids.has(stream_id)) {
            appearances.delete(entry_key);
        }
    }
}
